/**
 * The built-in toilet (defs/toilet.ts) as a data spec: a wall fixture
 * (POINT anchor + WALL constraint) whose whole drawing is proportional to
 * its footprint, so it is authored once at the default size in a single
 * `stretch` group:
 *  - the cistern against the wall (−n): full width, the back 30 % of the depth;
 *  - the seat hinge line at the cistern front, 80 % of the width;
 *  - the bowl: an ellipse filling the remaining depth, 75 % of the width,
 *    drawn as a closed 20-point polyline (the code's tessellation).
 */

import { OBJECT_DEFINITION_SCHEMA_VERSION, type ObjectDefinitionSpec, type Vec2 } from '../spec';

const W = 0.4;
const D = 0.7;
const HW = W / 2;
const HD = D / 2;
const CISTERN_FRONT = -HD + D * 0.3;
const BOWL_RADIUS_U = HW * 0.75;
const BOWL_CENTER_N = (CISTERN_FRONT + HD) / 2;
const BOWL_RADIUS_N = (HD - CISTERN_FRONT) / 2;
const BOWL_SEGMENTS = 20;

/** A closed ellipse outline as `segments` points (in the frame, at the default size). */
function ellipsePoints(centerN: number, radiusU: number, radiusN: number, segments: number): Vec2[] {
  const points: Vec2[] = [];
  for (let i = 0; i < segments; i++) {
    const a = (2 * Math.PI * i) / segments;
    points.push([radiusU * Math.cos(a), centerN + radiusN * Math.sin(a)]);
  }
  return points;
}

export const toiletSpec: ObjectDefinitionSpec = {
  schemaVersion: OBJECT_DEFINITION_SCHEMA_VERSION,
  anchor: 'POINT',
  constraint: { kind: 'WALL', defaultInset: 0 },
  defaultSize: { width: W, depth: D, height: 0.8 },
  params: [],
  shapes: [
    { id: 'cistern', name: 'Cistern', kind: 'rect', min: [-HW, -HD], max: [HW, CISTERN_FRONT] },
    {
      id: 'seatHinge',
      name: 'Seat hinge line',
      kind: 'polyline',
      points: [
        [-HW * 0.8, CISTERN_FRONT],
        [HW * 0.8, CISTERN_FRONT],
      ],
      closed: false,
    },
    { id: 'bowl', name: 'Bowl', kind: 'polyline', points: ellipsePoints(BOWL_CENTER_N, BOWL_RADIUS_U, BOWL_RADIUS_N, BOWL_SEGMENTS), closed: true },
  ],
  groups: [{ id: 'toilet', name: 'Toilet', shapeIds: ['cistern', 'seatHinge', 'bowl'], resize: { u: 'stretch', n: 'stretch' } }],
  model3d: null,
};
